/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { isNotEmpty, inflect } from "~/tools";
import { ApplicationOption, EnvironmentOption } from "../types";
import { Option } from "./types";

export function filterApps(applications: ApplicationOption[], filter: string) {
  return applications.filter(({ name }) =>
    isNotEmpty(filter)
      ? name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
      : applications
  );
}

export const getButtonLabel = (
  appsCount: number,
  envsCount: number,
  options: { capitalised: boolean } = { capitalised: true }
) => {
  if (appsCount + envsCount === 0) {
    return "Apply";
  }

  const appString = options.capitalised ? "Application" : "application";
  const envString = options.capitalised ? "Environment" : "environment";
  const selectedString = options.capitalised ? " Selected" : " selected";

  const appsLabel = appsCount
    ? `${appsCount} ${inflect(appString)(appsCount)}`
    : "";
  const envsLabel = envsCount
    ? `${envsCount} ${inflect(envString)(envsCount)}`
    : "";
  const conjuction = appsCount && envsCount ? " and " : "";
  const closure = appsCount || envsCount ? selectedString : "";
  return appsLabel + conjuction + envsLabel + closure;
};

export const getInputLabelOnlyEnvironments = (
  selectedApplications: Option[],
  applications: ApplicationOption[],
  envsCount: number
) => {
  const envs =
    applications
      ?.filter((app) => selectedApplications.find(({ nid }) => app.nid === nid))
      .flatMap((app) => app.environments) ?? [];

  const count = envs.length + envsCount;

  const envsLabel = count ? `${count} ${inflect("Environment")(count)}` : "";
  const closure = count ? " Selected" : "";

  return envsLabel + closure;
};

export const getInputLabel = (apps: Option[], envs: Option[]) => {
  const totalLength = apps.length + envs.length;

  if (totalLength === 0) {
    return "";
  }

  if (totalLength === 1) {
    return apps.length > 0 ? apps[0].name : envs[0].name;
  }

  return getButtonLabel(apps.length, envs.length);
};

export const generateSelectedApps = (
  selectedApplications: string[],
  applications: ApplicationOption[]
) => {
  return (
    applications
      ?.filter((app) => selectedApplications.includes(app?.nid ?? ""))
      .map((app) => {
        return {
          name: app?.name ?? "",
          nid: app?.nid ?? "",
        };
      }) ?? []
  );
};

export const generateSelectedEnvs = (
  selectedEnvironments: string[],
  applications: ApplicationOption[]
) => {
  const flatEnvitonments = applications?.flatMap((app) => app.environments);
  return (
    flatEnvitonments
      ?.filter((env) => selectedEnvironments.includes(env?.nid ?? ""))
      .map((env) => getEnvironmentNameNid(env)) || []
  );
};

export const getEnvironmentNameNid = (env: EnvironmentOption) => ({
  name: env?.name ?? "",
  nid: env?.nid ?? "",
});

export const isAppExpanded = ({
  nid,
  selectedEnvs,
  selectedApps,
  environments,
}: {
  nid: string;
  selectedEnvs: Option[];
  selectedApps: Option[];
  environments: EnvironmentOption[];
}) => {
  const appSelected = selectedApps.find((app) => app.nid === nid) !== undefined;
  if (appSelected) {
    return false;
  }
  const selectedEnvsCount = environments?.filter((environment) =>
    isEnvironmentSelected(environment, selectedEnvs)
  )?.length;

  return selectedEnvsCount > 0;
};

const isEnvironmentSelected = (
  environment: EnvironmentOption,
  selectedEnvs: Option[]
) => {
  return !!selectedEnvs.find((env) => env.nid === environment?.nid);
};
